import * as d3 from 'd3';
import { top_n_data, num_of_dataset } from '../data_processing/number_of_data.js';

/**
 * Create a progress bar using D3.js and append it to a specified HTML element.
 *
 * @param {number} cur_data_count The current count of data items.
 * @param {number} num_total_data The total number of data items.
 * @param {string} id The ID of the HTML element to which the progress bar should be appended.
 * @return {SVGElement} The SVG element containing the progress bar.
 */
export function progress_bar(cur_data_count, num_total_data, id) {
    let container = document.getElementById(id);
    let width = container.clientWidth; // 200;

    if(width == 0) {
        width = 350
    }

    let height = width / 8;
    const percentage = cur_data_count / num_total_data;

    const svg = d3.select(`#${id}`)
        .append("svg")
        .attr("width", width)
        .attr("height", height)
        .attr("overflow", "visible");

    svg.append("line")
        .attr("x1", 0)
        .attr("y1", height / 2)
        .attr("x2", width)
        .attr("y2", height / 2 )
        .attr("stroke", "Gainsboro")
        .attr("stroke-width", 10)
        .attr("stroke-linecap", "round");

    const progressLine = svg.append("line")
        .attr("x1", 0)
        .attr("y1", height / 2)
        .attr("x2", 0)
        .attr("y2", height / 2 )
        .attr("stroke", "rgba(0,194,165,1)")
        .attr("stroke-width", 10)
        .attr("stroke-linecap", "round");

    // Adding the animation
    progressLine.transition()
        .duration(1000) // Animation duration in milliseconds
        .attr("x2", width * percentage);

    return svg.node();
}

/**
 * Create a progress bar and append it to a specified HTML element (in a card).
 *
 * @param {Array} data The data array from a CSV file.
 * @param {string} target_column The name of the target column for the top N data.
 * @param {string} id The ID of the HTML element where the card and progress bar will be appended.
 */
export async function progress_bar_card(data, target_column, id, count_item) {
    let cur_data = top_n_data(data, 1, target_column, count_item);
    const num_total_data = await num_of_dataset(data, 0);
    let cur_data_name;
    let cur_data_count;

    if (Array.isArray(cur_data[0])) {
        cur_data_name = cur_data[0][0];
        cur_data_count = cur_data[0][1];
    } else if (typeof cur_data[0] === 'object') {
        cur_data_name = cur_data[0]['display_name'];
        cur_data_count = cur_data[0][count_item];
    }

    const cur_div = document.getElementById(id);
    if (cur_div) {
        cur_div.innerHTML = `
            <p>${cur_data_name}</p>
            <div class="col m-0">
                <span class="card-big-num fs-4">${cur_data_count}</span>
                <span class="card-sm-num">/ ${num_total_data}</span>
            </div>
        `;
        let bar = await progress_bar(cur_data_count, num_total_data, id);
        cur_div.append(bar);
    }
}

/**
 * Generates a progress bar and appends it to a specified HTML element.
 *
 * @param {Array} data - The data array from a CSV file.
 * @param {string} id - The ID of the HTML element where the progress bar will be appended.
 * @param {string} target_column - The name of the target column for the top N data.
 * @param {string} count_item - The name of the count item in the data array.
 * @param {Object} mapping_table - The mapping table for column names.
 */
export async function generate_progress_bar_div(data, id, target_column, count_item, mapping_table) {
    let cur_data = await top_n_data(data, 1, target_column, count_item);
    const num_total_data = await num_of_dataset(data, 0);

    let cur_data_name;
    let cur_data_count;

    if (Array.isArray(cur_data[0])) {
        cur_data_name = cur_data[0][0];
        cur_data_count = cur_data[0][1];
    } else if (typeof cur_data[0] === 'object') {
        cur_data_name = cur_data[0]['display_name'];
        cur_data_count = cur_data[0][count_item];

        if (target_column == 'keywords') {
            let label_value = cur_data[0]['zh_tw_label']['value'];
            cur_data_name = cur_data_name + "(" + label_value + ")";
        }
    }

    // Select the target div(container) and append a main dvi in it
    const container = document.getElementById(id);
    const main_div = document.createElement('div');
    main_div.className = 'mb-4';

    const h6 = document.createElement('h6');
    h6.setAttribute('data-i18n', `top_${target_column}`);
    h6.textContent = mapping_table[target_column];

    const span_title = document.createElement('span');
    span_title.className = 'overview-content-dark'
    span_title.textContent = `${cur_data_name}`

    const div_content = document.createElement('div');

    const span_big_num = document.createElement('span');
    span_big_num.className = 'card-big-num fs-4';
    span_big_num.textContent = `${cur_data_count}`
    const span_num = document.createElement('span');
    span_num.className = 'card-sm-num'
    span_num.textContent = `/ ${num_total_data}`

    const div_progress_bar = document.createElement('div');
    let bar = progress_bar(cur_data_count, num_total_data, id);
    div_progress_bar.append(bar);

    div_content.appendChild(span_big_num);
    div_content.appendChild(span_num);
    main_div.appendChild(h6);
    main_div.appendChild(span_title);
    main_div.appendChild(div_content);
    main_div.appendChild(div_progress_bar);
    container.append(main_div);

    $(container).localize();
}

/**
 * Generates a progress bar carousel item and appends it to a specified HTML element.
 *
 * @param {Array} data - The data array from a CSV file.
 * @param {string} id - The ID of the HTML element where the carousel item will be appended.
 * @param {string} target_column - The name of the target column for the top N data.
 * @param {string} count_method - The method used to count the data items.
 * @param {Object} mapping_table - The mapping table for column names.
 */
export async function generate_progress_bar_carousel_item(data, id, target_column, count_method, mapping_table) {
    const main_container = document.getElementById(id);
    
    const container = document.createElement('div');
    container.className = 'carousel-item';
    const container_id = 'top_' + target_column + '_item';
    container.id = container_id;
    //container.style = "margin-left: 6rem !important; margin-right:6em"

    const progress_bar = document.createElement('div');
    const progress_bar_id = 'top_' + target_column;
    progress_bar.id = progress_bar_id;

    main_container.appendChild(container);
    await generate_progress_bar_div(data, container_id, target_column, count_method, mapping_table);
    
    const first_divs_in_carousel_items = document.querySelectorAll('.carousel-item > div:first-of-type');
    first_divs_in_carousel_items.forEach(inner_div => { inner_div.style = "margin-left: 6rem !important; margin-right:6em" });
}